import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  ArrowLeft, Crown, Coins, Mail, StickyNote, LogIn, Ban, CheckCircle2,
  EyeOff, Eye, AlertTriangle, Save, Clock, Bot, Receipt, Trash2,
  Percent, Network, LifeBuoy, ShieldCheck, FileCheck, Image as ImageIcon,
  Award, BadgeCheck, Briefcase, UserRound,
} from "lucide-react";
import { Stat, Section, Pill, day, dt, money } from "./_shared";
import ReferralTree from "./ReferralTree";

const CLIENT_TIERS = [
  { v: "free", label: "Free" },
  { v: "plus", label: "Plus" },
  { v: "pro", label: "Pro" },
  { v: "unlimited", label: "Unlimited" },
];

const BADGES = [
  { v: "founder_50", label: "Founder 50", icon: Award },
  { v: "founder_500", label: "Founder 500", icon: Award },
  { v: "verified", label: "Verifiziert", icon: BadgeCheck },
];

const AFF_STATUS = [
  { v: "none", label: "Kein Affiliate" },
  { v: "pending", label: "In Prüfung" },
  { v: "active", label: "Aktiv" },
  { v: "rejected", label: "Abgelehnt" },
];

const APP_TONE = { approved: "good", pending: "warn", rejected: "bad", not_applied: "default" };
const SUP_TONE = { open: "warn", escalated: "bad", in_progress: "info", answered: "info", resolved: "good", closed: "default" };

function Card({ title, icon: Icon, children, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        {Icon && <Icon className="h-4 w-4 text-primary" />} {title}
      </div>
      {children}
    </div>
  );
}

export default function UserDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [txs, setTxs] = useState(null);
  const [net, setNet] = useState(null);

  // action form state
  const [clientTier, setClientTier] = useState("free");
  const [creditDelta, setCreditDelta] = useState("");
  const [role, setRole] = useState("");
  const [program, setProgram] = useState("");
  const [note, setNote] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [affStatus, setAffStatus] = useState("none");
  const [commission, setCommission] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [certImg, setCertImg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/users/${id}/detail`);
      setData(d);
      setRole(d.user?.role || "");
      setProgram(d.user?.program || "");
      setAffStatus(d.extras?.affiliate?.affiliate_status || "none");
      const aff = d.extras?.affiliate || {};
      setCommission(aff.commission_pct_override != null ? String(aff.commission_pct_override) : "");
      setClientTier(d.extras?.plan?.client_tier || "free");
    } catch (err) { toast.error(apiError(err)); }
    try {
      const { data: t } = await api.get(`/admin/users/${id}/transactions`);
      setTxs(t.transactions || []);
    } catch { /* ignore */ }
    try {
      const { data: n } = await api.get(`/admin/affiliate-network/${id}`);
      setNet(n);
    } catch { setNet(null); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, key) => {
    setBusy(key);
    try { await fn(); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-64 rounded-xl" /><Skeleton className="h-64 rounded-2xl" /></div>;
  if (!data) return <p className="text-sm text-muted-foreground">Nutzer nicht gefunden.</p>;

  const u = data.user || {};
  const x = data.extras || {};
  const sub = u.app_subscription || {};
  const founder = u.founder_grant || {};
  const rev = x.revenue || {};
  const act2 = x.activity || {};
  const flags = x.risk_flags || [];
  const notes = u.admin_notes || [];
  const aff = x.affiliate || {};
  const plan = x.plan || {};
  const badges = plan.badges || u.badges || [];
  const coachProActive = !!plan.coach_pro;
  const application = aff.application || {};
  const support = x.support || {};
  const cv = x.coach?.verification || u.coach_verification || null;
  const cvHasDoc = !!(cv && (cv.file_name || cv.image_mime || cv.image_base64 || cv.image_uploaded_at || cv.last_upload_at));

  const savePlanClient = () => act(async () => { await api.post(`/admin/users/${id}/plan`, { client_tier: clientTier, lifetime: true }); toast.success(`Client-Abo „${clientTier}" gesetzt`); }, "plan-client");
  const toggleCoachPro = (next) => act(async () => { await api.post(`/admin/users/${id}/plan`, { coach_pro: next, lifetime: true }); toast.success(next ? "Coach Pro aktiviert (Client-Unlimited inkl.)" : "Coach Pro deaktiviert"); }, "plan-coach");
  const toggleBadge = (badge, has) => act(async () => { await api.post(`/admin/users/${id}/badges`, { badge, action: has ? "remove" : "add" }); toast.success(has ? "Badge entfernt" : "Badge vergeben"); }, `badge-${badge}`);
  const grantFounder = (cohort) => act(async () => { await api.post(`/admin/users/${id}/grant-founder`, { cohort }); toast.success(`${cohort === "founder_500" ? "Founder 500" : "Founder 50"} vergeben`); }, `gf-${cohort}`);
  const revokeFounder = () => act(async () => { await api.post(`/admin/users/${id}/revoke-founder`); toast.success("Founder-Status entzogen"); }, "revoke-founder");
  const adjustCredits = () => act(async () => { await api.post(`/admin/users/${id}/credits`, { delta: parseInt(creditDelta, 10) || 0 }); setCreditDelta(""); toast.success("Credits angepasst"); }, "credits");
  const savePatch = () => act(async () => { await api.patch(`/admin/users/${id}`, { role, program }); toast.success("Gespeichert"); }, "patch");
  const setActive = (next) => act(async () => { await api.post(`/admin/users/${id}/set-active`, { active: next }); toast.success(next ? "Aktiviert" : "Gesperrt"); }, "active");
  const toggleHidden = () => act(async () => { await api.post(`/admin/users/${id}/toggle-hidden`); }, "hidden");
  const addNote = () => act(async () => { await api.post(`/admin/users/${id}/note`, { text: note }); setNote(""); toast.success("Notiz gespeichert"); }, "note");
  const sendEmail = () => act(async () => { const { data: r } = await api.post(`/admin/users/${id}/email`, { subject, message }); setSubject(""); setMessage(""); toast[r.ok ? "success" : "error"](r.ok ? "E-Mail gesendet" : "E-Mail fehlgeschlagen"); }, "email");
  const flagTx = (tx) => act(async () => { await api.post(`/admin/transactions/flag`, { source: tx.source, id: tx.txid, is_test: !tx.is_test }); }, `flag-${tx.txid}`);
  const delTx = (tx) => act(async () => { await api.post(`/admin/transactions/delete`, { source: tx.source, id: tx.txid }); toast.success("Transaktion gelöscht"); }, `del-${tx.txid}`);
  const setAssistant = (action) => act(async () => { await api.post(`/admin/users/${id}/assistant`, { action }); toast.success(action === "grant" ? "AI-Assistent gewährt" : "AI-Assistent entzogen"); }, `asst-${action}`);

  // affiliate controls
  const saveAffStatus = () => act(async () => { await api.post(`/admin/users/${id}/affiliate`, { affiliate_status: affStatus }); toast.success("Affiliate-Status gespeichert"); }, "aff-status");
  const saveCommission = () => act(async () => { await api.post(`/admin/users/${id}/affiliate`, { commission_pct: parseFloat(commission) }); toast.success("Provision gespeichert"); }, "aff-comm");
  const clearCommission = () => act(async () => { await api.post(`/admin/users/${id}/affiliate`, { clear_override: true }); setCommission(""); toast.success("Standard-Provision aktiv"); }, "aff-clear");
  const decideApp = (approve) => act(async () => { await api.post(`/admin/users/${id}/affiliate-application/decide`, { approve, rejection_reason: approve ? null : rejectReason || null }); toast.success(approve ? "Bewerbung freigegeben" : "Bewerbung abgelehnt"); }, `app-${approve}`);

  // coach verification
  const loadCert = () => act(async () => { const { data: img } = await api.get(`/admin/coach-verify/${id}/image`); setCertImg(img); if (!img?.image_base64) toast.info("Kein Dokument hochgeladen."); }, "cert");
  const decideVerify = (approve) => act(async () => { await api.post(`/admin/coach-verify/${id}/decide`, { approve, reason: approve ? null : rejectReason || null }); toast.success(approve ? "Verifizierung freigegeben" : "Verifizierung abgelehnt"); }, `verify-${approve}`);

  const impersonate = async () => {
    setBusy("imp");
    try {
      const { data: r } = await api.post(`/admin/users/${id}/impersonate`);
      localStorage.setItem("mw_imp_admin", tokenStore.access || "");
      localStorage.setItem("mw_imp_admin_refresh", tokenStore.refresh || "");
      localStorage.setItem("mw_imp_label", r.user?.name || r.user?.email || "Nutzer");
      tokenStore.set({ token: r.token });
      localStorage.removeItem("mw_refresh");
      window.location.href = "/app";
    } catch (err) { toast.error(apiError(err)); setBusy(null); }
  };

  const founderLabel = founder.cohort === "founder_500" ? "Founder 500" : founder.cohort === "founder_50" || u.founder_50 ? "Founder 50" : null;

  return (
    <div className="space-y-5" data-testid="admin-user-detail">
      <Link to="/app/admin/users" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="admin-user-back">
        <ArrowLeft className="h-4 w-4" /> Zurück zur Nutzerliste
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl text-foreground">{u.name || "—"}</h1>
          <p className="text-sm text-muted-foreground">{u.email} · {u.role}{u.active === false ? " · gesperrt" : ""}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium">{sub.tier || "free"}</span>
            {founderLabel && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">{founderLabel}</span>}
            {aff.is_affiliate && <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700">Affiliate</span>}
            {u.is_hidden_in_admin && <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-muted-foreground">versteckt</span>}
          </div>
        </div>
        <Button onClick={impersonate} variant="outline" className="gap-1.5 rounded-full" disabled={busy === "imp"} data-testid="admin-impersonate">
          <LogIn className="h-4 w-4" /> Als Nutzer einloggen
        </Button>
      </div>

      {/* Risk flags */}
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="admin-user-flags">
          {flags.map((f) => (
            <span key={f.key} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${f.severity === "high" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
              <AlertTriangle className="h-3.5 w-3.5" /> {f.label}
            </span>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Registriert" value={u.created_at ? day(u.created_at) : "—"} testId="admin-stat-created" />
        <Stat label="Zuletzt aktiv" value={act2.days_since_seen == null ? "nie" : act2.days_since_seen <= 0 ? "heute" : `vor ${act2.days_since_seen} T`} tone={act2.active_7d ? "good" : act2.active_30d ? "default" : "warn"} testId="admin-stat-lastseen" />
        <Stat label="Plan" value={sub.tier || "free"} />
        <Stat label="Credits" value={u.credits ?? 0} />
        <Stat label={u.role === "coach" ? "Verdient" : "Ausgegeben"} value={money(u.role === "coach" ? (rev.earned_cents || 0) : (rev.spent_cents || 0))} />
        <Stat label="Empfehlungen" value={aff.referred_count ?? x.referral?.referred_count ?? 0} testId="admin-stat-referrals" />
      </div>

      {/* Affiliate referral stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Link geteilt" value={aff.share_count ?? 0} hint="Share-Card-Events" testId="admin-stat-shares" />
        <Stat label="Über Link gekommen" value={aff.referred_count ?? 0} testId="admin-stat-referred" />
        <Stat label="Davon mit Abo" value={aff.referred_subscribed_count ?? 0} tone="good" testId="admin-stat-referred-subbed" />
        <Stat label="Provision" value={`${aff.commission_pct ?? 0}%`} hint={aff.commission_pct_override != null ? "individuell" : "Standard"} tone="info" testId="admin-stat-commission" />
      </div>

      {/* Most-engaged areas */}
      {(x.top_areas?.length || 0) > 0 && (
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="admin-user-areas">
          <p className="mb-2 text-sm font-semibold text-foreground">Aktivste Bereiche</p>
          <div className="flex flex-wrap gap-2">
            {x.top_areas.map((a) => (
              <span key={a.area} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs text-foreground">
                {a.area} <span className="font-semibold text-primary">{a.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Affiliate control */}
      <Card title="Affiliate-Steuerung" icon={Percent} testId="admin-card-affiliate">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status & Provision</p>
            <div className="flex flex-wrap items-center gap-2">
              <select value={affStatus} onChange={(e) => setAffStatus(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-2 text-sm" data-testid="admin-aff-status">
                {AFF_STATUS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
              <Button size="sm" variant="outline" className="rounded-full" disabled={busy === "aff-status"} onClick={saveAffStatus} data-testid="admin-aff-status-save">Status speichern</Button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="relative">
                <Input type="number" step="0.5" min="0" max="100" value={commission} onChange={(e) => setCommission(e.target.value)} placeholder={`${aff.commission_pct_default ?? 0}`} className="w-28 pr-7" data-testid="admin-aff-commission" />
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
              </div>
              <Button size="sm" className="rounded-full" disabled={busy === "aff-comm" || commission === ""} onClick={saveCommission} data-testid="admin-aff-commission-save">Provision setzen</Button>
              <Button size="sm" variant="outline" className="rounded-full" disabled={busy === "aff-clear"} onClick={clearCommission} data-testid="admin-aff-commission-clear">Standard ({aff.commission_pct_default ?? 0}%)</Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Aktuell wirksam: <span className="font-semibold text-foreground">{aff.commission_pct ?? 0}%</span> · {aff.program_label || "Standard"}
              {aff.commission_pct_override != null ? " · individuell gesetzt" : " · Standard-Satz"}
            </p>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Bewerbung</p>
            <div className="flex items-center gap-2 text-sm">
              <Pill tone={APP_TONE[aff.application_status] || "default"}>
                {aff.application_status === "approved" ? "Freigegeben" : aff.application_status === "pending" ? "In Prüfung" : aff.application_status === "rejected" ? "Abgelehnt" : "Nicht beworben"}
              </Pill>
              {application.submitted_at && <span className="text-xs text-muted-foreground">eingereicht {day(application.submitted_at)}</span>}
            </div>
            {(application.social_links || []).length > 0 && (
              <ul className="mt-2 space-y-1">
                {application.social_links.map((s, i) => (
                  <li key={i} className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{s.platform}</span> · {s.handle_or_url} · {Number(s.follower_count || 0).toLocaleString("de-DE")} Follower
                  </li>
                ))}
              </ul>
            )}
            {application.audience_pitch && <p className="mt-2 rounded-lg bg-secondary/50 p-2 text-xs text-foreground">{application.audience_pitch}</p>}
            {application.rejection_reason && <p className="mt-1 text-xs text-rose-600">Ablehnung: {application.rejection_reason}</p>}
            {aff.application_status === "pending" && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" className="rounded-full" disabled={busy === "app-true"} onClick={() => decideApp(true)} data-testid="admin-app-approve">Freigeben</Button>
                <Button size="sm" variant="outline" className="rounded-full text-rose-600" disabled={busy === "app-false"} onClick={() => decideApp(false)} data-testid="admin-app-reject">Ablehnen</Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Action cards */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Client-Abo" icon={UserRound} testId="admin-card-client-sub">
          <div className="flex flex-wrap items-center gap-2">
            <select value={clientTier} onChange={(e) => setClientTier(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-2 text-sm" data-testid="admin-client-tier" disabled={coachProActive}>
              {CLIENT_TIERS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
            <Button size="sm" className="rounded-full" disabled={busy === "plan-client" || coachProActive} onClick={savePlanClient} data-testid="admin-save-client-tier">Client-Abo setzen</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Aktueller Client-Bereich: <span className="font-semibold text-foreground">{coachProActive ? "Unlimited (via Coach Pro)" : (plan.client_tier || "free")}</span>
            {coachProActive ? " · Coach Pro schaltet Unlimited automatisch frei" : plan.lifetime ? " · lifetime" : ""}
          </p>
        </Card>

        <Card title="Coach-Abo (Coach Pro)" icon={Briefcase} testId="admin-card-coach-sub">
          {u.role === "coach" ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => toggleCoachPro(!coachProActive)}
                  disabled={busy === "plan-coach"}
                  data-testid="admin-toggle-coach-pro"
                  className={`inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors ${coachProActive ? "bg-emerald-600 text-white" : "bg-secondary text-foreground hover:bg-accent"}`}
                >
                  {coachProActive ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                  Coach Pro {coachProActive ? "aktiv" : "inaktiv"}
                </button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Coach Pro schaltet im Client-Bereich automatisch Unlimited frei. Voraussetzung für das Zusatzpaket AI-Praxis-Assistent.</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{`Nur für Coaches. Dieser Nutzer hat die Rolle „${u.role}".`}</p>
          )}
        </Card>

        <Card title="Founder-Status" icon={Crown} testId="admin-card-founder">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" className="rounded-full" disabled={busy === "gf-founder_50"} onClick={() => grantFounder("founder_50")} data-testid="admin-grant-f50">Founder 50 geben</Button>
            <Button size="sm" className="rounded-full" disabled={busy === "gf-founder_500"} onClick={() => grantFounder("founder_500")} data-testid="admin-grant-f500">Founder 500 geben</Button>
            <Button size="sm" variant="outline" className="rounded-full text-rose-600" disabled={busy === "revoke-founder"} onClick={revokeFounder} data-testid="admin-revoke-founder">Entziehen</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {founderLabel ? `Aktuell: ${founderLabel}` : "Kein Founder-Status."} Founder = Client Unlimited{u.role === "coach" ? " + Coach Pro" : ""}, lebenslang. Vergibt automatisch das passende Badge.
          </p>
        </Card>

        {u.role === "coach" && (
          <Card title="AI-Praxis-Assistent (Zusatzpaket)" icon={Bot} testId="admin-card-assistant">
            <p className="text-sm text-foreground">Status: <span className="font-semibold">{x.assistant?.active ? "Aktiv" : "Inaktiv"}</span>{x.assistant?.status ? ` (${x.assistant.status})` : ""}{x.assistant?.source ? ` · ${x.assistant.source}` : ""}</p>
            {!coachProActive && <p className="mt-1 text-xs text-amber-600">Nur mit Coach Pro möglich — bitte zuerst Coach Pro aktivieren.</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" className="rounded-full" disabled={busy === "asst-grant" || !coachProActive} onClick={() => setAssistant("grant")} data-testid="admin-assistant-grant">Gratis gewähren</Button>
              <Button size="sm" variant="outline" className="rounded-full text-rose-600" disabled={busy === "asst-revoke"} onClick={() => setAssistant("revoke")} data-testid="admin-assistant-revoke">Entziehen</Button>
            </div>
          </Card>
        )}

        <Card title="Badges" icon={Award} testId="admin-card-badges">
          <div className="flex flex-wrap gap-2">
            {BADGES.map((b) => {
              const has = badges.includes(b.v);
              const Icon = b.icon;
              return (
                <button
                  key={b.v}
                  onClick={() => toggleBadge(b.v, has)}
                  disabled={busy === `badge-${b.v}`}
                  data-testid={`admin-badge-${b.v}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${has ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-accent"}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {b.label}{has ? " ✓" : ""}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{`Klick = zuweisen/entfernen. „Verifiziert" setzt auch die Verifiziert-Anzeige; falls noch nicht per Dokument erledigt, wird der Coach manuell als verifiziert markiert.`}</p>
        </Card>

        <Card title="Credits" icon={Coins} testId="admin-card-credits">
          <div className="flex flex-wrap items-center gap-2">
            <Input type="number" value={creditDelta} onChange={(e) => setCreditDelta(e.target.value)} placeholder="+/- z. B. 50" className="w-32" data-testid="admin-credit-delta" />
            <Button size="sm" className="rounded-full" disabled={busy === "credits" || !creditDelta} onClick={adjustCredits} data-testid="admin-credit-apply">Anpassen</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Guthaben: {u.credits ?? 0} Credits</p>
        </Card>

        <Card title="Rolle & Programm · Status" icon={Save} testId="admin-card-rolestatus">
          <div className="flex flex-wrap items-center gap-2">
            <select value={role} onChange={(e) => setRole(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-2 text-sm" data-testid="admin-role">
              <option value="client">Client</option><option value="coach">Coach</option><option value="admin">Admin</option>
            </select>
            <Input value={program} onChange={(e) => setProgram(e.target.value)} placeholder="Programm-Tag" className="w-40" data-testid="admin-program" />
            <Button size="sm" variant="outline" className="rounded-full" disabled={busy === "patch"} onClick={savePatch} data-testid="admin-save-patch">Speichern</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {u.active === false
              ? <Button size="sm" variant="outline" className="gap-1.5 rounded-full text-emerald-600" disabled={busy === "active"} onClick={() => setActive(true)} data-testid="admin-activate"><CheckCircle2 className="h-4 w-4" /> Aktivieren</Button>
              : <Button size="sm" variant="outline" className="gap-1.5 rounded-full text-rose-600" disabled={busy === "active"} onClick={() => setActive(false)} data-testid="admin-suspend"><Ban className="h-4 w-4" /> Sperren</Button>}
            <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy === "hidden"} onClick={toggleHidden} data-testid="admin-toggle-hidden">
              {u.is_hidden_in_admin ? <><Eye className="h-4 w-4" /> Einblenden</> : <><EyeOff className="h-4 w-4" /> Verstecken</>}
            </Button>
          </div>
        </Card>

        <Card title="E-Mail senden" icon={Mail} testId="admin-card-email">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Betreff" className="mb-2" data-testid="admin-email-subject" />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Nachricht…" rows={3} className="w-full rounded-lg border border-border bg-background p-2 text-sm" data-testid="admin-email-message" />
          <Button size="sm" className="mt-2 rounded-full" disabled={busy === "email" || !subject || !message} onClick={sendEmail} data-testid="admin-email-send">Senden</Button>
        </Card>

        <Card title="Interne Notiz" icon={StickyNote} testId="admin-card-note">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz für das Team…" rows={3} className="w-full rounded-lg border border-border bg-background p-2 text-sm" data-testid="admin-note-text" />
          <Button size="sm" className="mt-2 rounded-full" disabled={busy === "note" || !note} onClick={addNote} data-testid="admin-note-add">Notiz hinzufügen</Button>
          {notes.length > 0 && (
            <ul className="mt-3 space-y-2">
              {notes.slice(0, 5).map((n, i) => (
                <li key={i} className="rounded-lg bg-secondary/50 p-2 text-xs">
                  <p className="text-foreground">{n.text}</p>
                  <p className="mt-0.5 text-muted-foreground">{n.by} · {dt(n.ts)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Coach verification + documents — auch für Clients zeigen, die ein
          Zertifikat hochgeladen haben (sie sind bis zur Freigabe role=client). */}
      {(u.role === "coach" || cv) && (
        <Section title="Coach-Verifizierung & Dokumente" testId="admin-user-verification">
          {cv ? (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <Pill tone={cv.status === "approved" ? "good" : cv.status === "rejected" ? "bad" : "warn"}>{cv.status || "unverified"}</Pill>
                {cv.submitted_at && <span className="text-xs text-muted-foreground">eingereicht {dt(cv.submitted_at)}</span>}
                {cv.dpa_accepted && <span className="text-xs text-emerald-600">DPA akzeptiert</span>}
              </div>
              {cv.ai_verdict && (
                <div className="rounded-lg bg-secondary/50 p-3 text-xs">
                  <p className="font-semibold text-foreground">KI-Prüfung</p>
                  <p className="mt-1 text-muted-foreground">
                    Zertifikat: {cv.ai_verdict.is_certificate ? "ja" : "nein"} · Konfidenz: {Math.round((cv.ai_verdict.confidence || 0) * 100)}% · Bereich: {cv.ai_verdict.subject_area || "—"}
                  </p>
                  {cv.ai_verdict.issuing_body && <p className="text-muted-foreground">Aussteller: {cv.ai_verdict.issuing_body}</p>}
                  {cv.ai_verdict.holder_name && <p className="text-muted-foreground">Inhaber: {cv.ai_verdict.holder_name}</p>}
                  {cv.ai_verdict.notes && <p className="mt-1 italic text-muted-foreground">{cv.ai_verdict.notes}</p>}
                </div>
              )}
              {cv.admin_verdict && (
                <p className="text-xs text-muted-foreground">Admin-Entscheid: {cv.admin_verdict.approved ? "freigegeben" : "abgelehnt"}{cv.admin_verdict.reason ? ` · ${cv.admin_verdict.reason}` : ""}</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {cvHasDoc ? (
                  <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy === "cert"} onClick={loadCert} data-testid="admin-verify-loaddoc">
                    <ImageIcon className="h-4 w-4" /> {busy === "cert" ? "Lädt …" : "Dokument anzeigen"}
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground" data-testid="admin-verify-nodoc">Kein Dokument hochgeladen (z. B. Bestands-Coach).</span>
                )}
                {cv.status !== "approved" && (
                  <Button size="sm" className="gap-1.5 rounded-full" disabled={busy === "verify-true"} onClick={() => decideVerify(true)} data-testid="admin-verify-approve">
                    <FileCheck className="h-4 w-4" /> Freigeben
                  </Button>
                )}
                {cv.status !== "rejected" && (
                  <Button size="sm" variant="outline" className="rounded-full text-rose-600" disabled={busy === "verify-false"} onClick={() => decideVerify(false)} data-testid="admin-verify-reject">Ablehnen</Button>
                )}
              </div>
              {certImg && !certImg.image_base64 && (
                <p className="text-xs text-amber-600" data-testid="admin-verify-empty">Kein Dokument vorhanden.</p>
              )}
              {certImg?.image_base64 && (() => {
                const mime = certImg.image_mime || "image/jpeg";
                const src = `data:${mime};base64,${certImg.image_base64}`;
                const isPdf = mime.includes("pdf");
                return (
                  <div className="space-y-2" data-testid="admin-verify-doc">
                    {isPdf ? (
                      <iframe src={src} title={certImg.file_name || "Dokument"} className="h-[480px] w-full rounded-xl border border-border" data-testid="admin-verify-pdf" />
                    ) : (
                      <img src={src} alt={certImg.file_name || "Zertifikat"} className="max-h-[480px] rounded-xl border border-border" data-testid="admin-verify-image" />
                    )}
                    <a href={src} target="_blank" rel="noreferrer" download={certImg.file_name || "dokument"} className="inline-block text-xs font-medium text-primary hover:underline" data-testid="admin-verify-opennew">
                      {certImg.file_name || "Dokument"} · in neuem Tab öffnen / herunterladen
                    </a>
                  </div>
                );
              })()}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Keine Verifizierung eingereicht.</p>
          )}
        </Section>
      )}

      {/* Support requests */}
      <Section title={`Support-Anfragen (${support.total || 0} · ${support.open_count || 0} offen)`} testId="admin-user-support">
        {(support.items?.length || 0) > 0 ? (
          <div className="space-y-2">
            {support.items.map((s) => (
              <div key={`${s.system}-${s.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3 text-sm" data-testid={`admin-support-${s.id}`}>
                <LifeBuoy className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{s.subject || s.category || "Anfrage"}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.system === "coach_ticket" ? "Coach-Ticket" : "Support"}{s.category ? ` · ${s.category}` : ""}{s.priority ? ` · ${s.priority}` : ""} · {s.updated_at ? dt(s.updated_at) : "—"}
                  </p>
                </div>
                <Pill tone={SUP_TONE[s.status] || "default"}>{s.status}</Pill>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Keine Support-Anfragen.</p>
        )}
      </Section>

      {/* Referral network — 5 levels */}
      {net && (
        <Section title="Empfehlungs-Netzwerk (5 Ebenen)" testId="admin-user-network">
          <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Network className="h-4 w-4 text-primary" />
            Gesamt: <span className="font-semibold text-foreground">{net.affiliate?.total_network_size ?? 0}</span> im Netzwerk ·
            Direkt: <span className="font-semibold text-foreground">{net.affiliate?.direct_ref_count ?? 0}</span> ·
            Score: <span className="font-semibold text-foreground">{net.affiliate?.performance_score ?? 0}</span>
          </div>
          <ReferralTree tree={net.tree} perLevel={net.per_level} totals={net.totals} potential={net.potential} />
        </Section>
      )}

      {/* Transactions */}
      {txs && txs.length > 0 && (
        <Section title="Transaktionen" testId="admin-user-transactions">
          <div className="space-y-2">
            {txs.map((t) => (
              <div key={t.source + t.txid} data-testid={`admin-tx-${t.txid}`} className={`flex flex-wrap items-center gap-3 rounded-xl border p-3 text-sm ${t.is_test ? "border-dashed border-amber-300 bg-amber-50/50" : "border-border bg-card"}`}>
                <Receipt className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{t.label || t.source}</p>
                  <p className="text-xs text-muted-foreground">{t.status} · {t.created_at ? dt(t.created_at) : "—"}{t.credits ? ` · ${t.credits} Credits` : ""}</p>
                </div>
                <div className="font-medium text-foreground">{money(t.amount_cents || 0)}</div>
                <button onClick={() => flagTx(t)} disabled={busy === `flag-${t.txid}`} data-testid={`admin-tx-flag-${t.txid}`} className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${t.is_test ? "bg-amber-200 text-amber-900" : "bg-secondary text-muted-foreground hover:bg-accent"}`}>
                  {t.is_test ? "Testtransaktion" : "Reale Transaktion"}
                </button>
                {t.deletable && (
                  <button onClick={() => delTx(t)} disabled={busy === `del-${t.txid}`} data-testid={`admin-tx-delete-${t.txid}`} className="rounded-full p-1.5 text-rose-600 transition-colors hover:bg-rose-50" title="Dauerhaft löschen">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Als Testtransaktion markierte Einträge werden aus Umsatz/LTV ausgeschlossen. Dauerhaftes Löschen nur für Transaktionen vor dem 03.10.2026.</p>
        </Section>
      )}

      {/* Timeline + recent activity */}
      {(x.timeline?.length || x.recent_activity?.length) ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {x.timeline?.length ? (
            <Section title="Verlauf" testId="admin-user-timeline">
              <ul className="space-y-2">
                {x.timeline.map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="flex-1 text-foreground">{t.label}</span>
                    <span className="text-xs text-muted-foreground">{day(t.ts)}</span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
          {x.recent_activity?.length ? (
            <Section title="Letzte Admin-Aktivität" testId="admin-user-activity">
              <ul className="space-y-2">
                {x.recent_activity.map((a, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="flex-1 text-foreground">{a.action}</span>
                    <span className="text-xs text-muted-foreground">{a.actor_email} · {dt(a.ts)}</span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
